// Lógica de importación: lee la plantilla (hoja única "Datos" + hoja de
// referencia "Modalidad y Categoría") y produce, por cada fila con datos,
// un objeto de inscripción listo para guardar, o una lista de problemas.
//
// Dos niveles de problema por fila:
//   - errores: bloquean la fila (y por lo tanto todo el lote, ver
//     RegistryImportWizard). Campos obligatorios faltantes, formatos
//     inválidos, o la modalidad/categoría no se pudo resolver.
//   - advertencias: NO bloquean. Ocurren cuando un valor de un campo tipo
//     lista (Select/checkbox) no coincide con ninguna opción conocida; el
//     valor se guarda tal como se escribió, para no frenar una migración
//     por texto libre que no calza exacto.
//
// No depende de React ni escribe nada en Firebase.

import { getCategoriaDetalle, getDynamicColumnsForModalidad } from "./registryImportColumns";

const HEADER_KEY_ROW = 1;
const FIRST_DATA_ROW = 3;

const MONEDA_BASE_DEFAULT = {
  codigo: "MXN",
  tipo: "base",
  tipoCambio: 1,
  texto: "Pesos Méxicanos",
  cambioAPeso: "1",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TELEFONO_REGEX = /^[0-9]{10,15}$/;

function normalizar(valor) {
  if (valor === undefined || valor === null) return "";
  // Excel suele autoconvertir correos/URLs escritos en una celda en un
  // hipervínculo; ExcelJS entonces entrega { text, hyperlink } en vez de un
  // string plano. Igual puede llegar como texto enriquecido { richText: [...] }.
  if (typeof valor === "object") {
    if (typeof valor.text === "string") return valor.text.trim();
    if (Array.isArray(valor.richText)) {
      return valor.richText.map((rt) => rt.text || "").join("").trim();
    }
    if (valor.result !== undefined) return normalizar(valor.result); // celda con fórmula
    return "";
  }
  return String(valor).trim();
}

function comparable(valor) {
  // Sin distinguir mayúsculas ni acentos, para que "Si"/"sí"/"SI", o
  // "Mexico"/"México", se reconozcan como el mismo valor.
  return normalizar(valor)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Coincidencia exacta (sin distinguir mayúsculas/acentos de caja) contra una
// lista de opciones conocidas. Ya no hay hoja de equivalencias: si no
// coincide, quien llama decide si eso es advertencia o error.
function coincidenciaExacta(valorTexto, opciones) {
  return (opciones || []).find((o) => comparable(o) === comparable(valorTexto)) || null;
}

function excelValorAFechaISO(valor) {
  if (valor instanceof Date) {
    const y = valor.getFullYear();
    const m = String(valor.getMonth() + 1).padStart(2, "0");
    const d = String(valor.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const texto = normalizar(valor);
  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return texto;
  return null;
}

// Lee la hoja "Datos" (única, todas las modalidades) y devuelve las filas
// crudas, sin validar todavía. Columnas cuya fila 1 no tiene una clave
// reconocida (o está vacía) simplemente no se leen: así se pueden pegar
// columnas propias del archivo de origen que no se vayan a usar.
export function readTemplateWorkbook(workbook) {
  const sheet = workbook.getWorksheet("Datos");
  if (!sheet) {
    throw new Error(
      'Este archivo no tiene una hoja "Datos". Verifica que sea la plantilla generada con el botón "Plantilla de importación (Excel)" de este evento.'
    );
  }

  const keyRow = sheet.getRow(HEADER_KEY_ROW);
  const keysByCol = {};
  keyRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const key = normalizar(cell.value);
    if (key) keysByCol[colNumber] = key;
  });

  const filas = [];
  const lastRow = sheet.actualRowCount || sheet.rowCount;

  for (let rowNumber = FIRST_DATA_ROW; rowNumber <= lastRow; rowNumber++) {
    const row = sheet.getRow(rowNumber);
    const rawValues = {};
    let tieneAlgunDato = false;

    Object.keys(keysByCol).forEach((colNumberStr) => {
      const colNumber = Number(colNumberStr);
      const key = keysByCol[colNumber];
      const cell = row.getCell(colNumber);
      const valor = cell ? cell.value : null;
      rawValues[key] = valor;
      if (valor !== null && valor !== undefined && normalizar(valor) !== "") {
        tieneAlgunDato = true;
      }
    });

    if (!tieneAlgunDato) continue;
    filas.push({ rowNumber, rawValues });
  }

  return filas;
}

// Lee la hoja "Modalidad y Categoría" y arma el índice de búsqueda:
// etiqueta normalizada -> { codigoModalidad, codigoCategoria, rama }.
export function readModalidadCategoriaLookup(workbook) {
  const sheet = workbook.getWorksheet("Modalidad y Categoría");
  if (!sheet) {
    throw new Error(
      'Este archivo no tiene la hoja "Modalidad y Categoría". Verifica que sea la plantilla generada con el botón "Plantilla de importación (Excel)" de este evento.'
    );
  }

  const lookup = {};
  const lastRow = sheet.actualRowCount || sheet.rowCount;

  for (let r = 2; r <= lastRow; r++) {
    const row = sheet.getRow(r);
    const etiqueta = normalizar(row.getCell(1).value);
    const codigoModalidad = normalizar(row.getCell(3).value);
    const codigoCategoria = normalizar(row.getCell(4).value);
    const rama = normalizar(row.getCell(5).value);
    if (!etiqueta || !codigoModalidad || !rama) continue;

    lookup[comparable(etiqueta)] = { codigoModalidad, codigoCategoria, rama };
  }

  return lookup;
}

function validarCamposDinamicos(columns, rawValues, categoriaMatch) {
  const errores = [];
  const advertencias = [];
  const camposDinamicos = {};

  columns.forEach((col) => {
    if (col.group !== "general" && col.group !== "grupo") return;

    const aplica =
      col.group === "general" ||
      (categoriaMatch && categoriaMatch.camposAdicionalesKey === col.grupoKey);
    if (!aplica) return;

    const valorTexto = normalizar(rawValues[col.key]);
    if (!valorTexto) {
      if (col.required) errores.push(`El campo "${col.label}" es obligatorio.`);
      return;
    }

    if (col.type === "select" || col.type === "checkbox") {
      const opcion = coincidenciaExacta(valorTexto, col.options);
      if (!opcion) {
        advertencias.push(
          `El valor "${valorTexto}" no coincide con ninguna opción conocida para "${col.label}"; se guardará tal como se escribió.`
        );
        camposDinamicos[col.fieldKey] =
          col.type === "checkbox" ? comparable(valorTexto) === "si" : valorTexto;
      } else if (col.type === "checkbox") {
        camposDinamicos[col.fieldKey] = comparable(opcion) === "si";
      } else {
        camposDinamicos[col.fieldKey] = opcion;
      }
    } else {
      camposDinamicos[col.fieldKey] = valorTexto;
    }
  });

  return { errores, advertencias, camposDinamicos };
}

function validarProductos(columns, rawValues) {
  const errores = [];
  const advertencias = [];
  const itemsOrden = [];

  const columnasPorProducto = {};
  columns.forEach((col) => {
    if (col.group !== "producto") return;
    if (!columnasPorProducto[col.productoCodigo]) columnasPorProducto[col.productoCodigo] = [];
    columnasPorProducto[col.productoCodigo].push(col);
  });

  Object.keys(columnasPorProducto).forEach((codigoStr) => {
    const cols = columnasPorProducto[codigoStr];
    const colCantidad = cols.find((c) => c.esCantidadProducto);
    const colsCampos = cols.filter((c) => !c.esCantidadProducto);
    if (!colCantidad) return;

    const cantidadTexto = normalizar(rawValues[colCantidad.key]);
    let cantidad = 0;
    if (cantidadTexto) {
      const parsed = Number(cantidadTexto);
      if (!Number.isInteger(parsed) || parsed < 0) {
        errores.push(`"${colCantidad.label}" debe ser un número entero mayor o igual a 0.`);
        return;
      }
      cantidad = parsed;
    }

    if (cantidad <= 0) return;

    if (colCantidad.maxCantidad !== undefined && cantidad > colCantidad.maxCantidad) {
      errores.push(
        `"${colCantidad.productoNombre}" admite máximo ${colCantidad.maxCantidad} unidad(es) (se indicó ${cantidad}).`
      );
    }

    const extraData = {};
    colsCampos.forEach((col) => {
      const valorTexto = normalizar(rawValues[col.key]);
      if (!valorTexto) {
        if (col.required) {
          errores.push(
            `El campo "${col.label}" es obligatorio porque la cantidad de "${colCantidad.productoNombre}" es mayor a 0.`
          );
        }
        return;
      }
      if (col.type === "select" || col.type === "checkbox") {
        const opcion = coincidenciaExacta(valorTexto, col.options);
        if (!opcion) {
          advertencias.push(
            `El valor "${valorTexto}" no coincide con ninguna opción conocida para "${col.label}"; se guardará tal como se escribió.`
          );
          extraData[col.fieldKey] = valorTexto;
        } else {
          extraData[col.fieldKey] = opcion;
        }
      } else {
        extraData[col.fieldKey] = valorTexto;
      }
    });

    itemsOrden.push({
      code: `producto-${colCantidad.productoCodigo}`,
      description: colCantidad.productoDescripcion || colCantidad.productoNombre,
      name: colCantidad.productoNombre,
      precioKey: "",
      quantity: cantidad,
      unit_price: colCantidad.precioProducto || 0,
      ...(Object.keys(extraData).length > 0 ? { extraData } : {}),
    });
  });

  return { errores, advertencias, itemsOrden };
}

// Valida y construye el objeto de inscripción para todas las filas leídas.
// numerosExistentes: Set de folios ya usados por inscripciones existentes
// (no borradas) en el evento, para detectar choques al importar "Número".
export function buildImportPreview({
  filas,
  eventSubEventsRegistry,
  registryConfiguration,
  eventDetail,
  numerosExistentes,
  modalidadCategoriaLookup,
}) {
  const numerosUsadosEnLote = new Set();
  const resultados = [];

  filas.forEach((fila) => {
    const { rowNumber, rawValues } = fila;
    const errores = [];
    const advertencias = [];

    const separarApellidos = !!(
      registryConfiguration.configuracion && registryConfiguration.configuracion.separarApellidos
    );

    const nombres = normalizar(rawValues.nombres);
    if (!nombres) errores.push("El nombre es obligatorio.");

    let apellidos = "";
    let apellidoPaterno = "";
    let apellidoMaterno = "";
    if (separarApellidos) {
      apellidoPaterno = normalizar(rawValues.apellidoPaterno);
      apellidoMaterno = normalizar(rawValues.apellidoMaterno);
      if (!apellidoPaterno) errores.push("El apellido paterno es obligatorio.");
    } else {
      apellidos = normalizar(rawValues.apellidos);
      if (!apellidos) errores.push("El apellido es obligatorio.");
    }

    const fechaISO = excelValorAFechaISO(rawValues.fechaNacimiento);
    if (!fechaISO) errores.push("La fecha de nacimiento no es válida (usa AAAA-MM-DD).");

    const correo = normalizar(rawValues.correo);
    if (!correo) errores.push("El correo es obligatorio.");
    else if (!EMAIL_REGEX.test(correo)) errores.push("El correo no tiene un formato válido.");

    const telefono = normalizar(rawValues.telefono);
    if (!telefono) errores.push("El teléfono es obligatorio.");
    else if (!TELEFONO_REGEX.test(telefono)) errores.push("El teléfono debe tener entre 10 y 15 dígitos.");

    // Resolver modalidad + categoría + género vía la hoja "Modalidad y Categoría".
    const modalidadCategoriaTexto = normalizar(rawValues.modalidadCategoria);
    let modalidad = null;
    let categoriaMatch = null;

    if (!modalidadCategoriaTexto) {
      errores.push('La columna "Modalidad y Categoría" es obligatoria.');
    } else {
      const resuelto = modalidadCategoriaLookup[comparable(modalidadCategoriaTexto)];
      if (!resuelto) {
        errores.push(
          `No se reconoce "${modalidadCategoriaTexto}" en la hoja "Modalidad y Categoría". Cópialo exactamente de ahí, o agrega una fila nueva si es una combinación que falta.`
        );
      } else {
        modalidad = eventSubEventsRegistry["modalidad_" + resuelto.codigoModalidad];
        if (!modalidad) {
          errores.push(
            `La modalidad con código ${resuelto.codigoModalidad} ya no existe en este evento. Genera una plantilla nueva.`
          );
        } else {
          const detalle = getCategoriaDetalle(modalidad);
          categoriaMatch = detalle.find(
            (c) => String(c.categoria) === String(resuelto.codigoCategoria) && c.rama === resuelto.rama
          );
          if (!categoriaMatch) {
            errores.push(
              `La categoría de "${modalidadCategoriaTexto}" ya no existe en esta modalidad. Genera una plantilla nueva.`
            );
          }
        }
      }
    }

    let numero = null;
    const numeroTexto = normalizar(rawValues.numero);
    if (numeroTexto) {
      const parsed = Number(numeroTexto);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        errores.push("El número debe ser un entero positivo.");
      } else if (numerosUsadosEnLote.has(parsed) || (numerosExistentes && numerosExistentes.has(parsed))) {
        errores.push(`El número ${parsed} ya está siendo usado por otra inscripción.`);
      } else {
        numero = parsed;
        numerosUsadosEnLote.add(parsed);
      }
    }

    let pagoExterno = false;
    const pagoExternoTexto = normalizar(rawValues.pagoExterno);
    if (pagoExternoTexto) {
      const opcion = coincidenciaExacta(pagoExternoTexto, ["No", "Sí"]);
      if (!opcion) {
        advertencias.push(
          `El valor "${pagoExternoTexto}" de "Pago recibido fuera de Dashport" no es "Sí" ni "No"; se asumirá "No".`
        );
      } else {
        pagoExterno = comparable(opcion) === "si";
      }
    }

    let camposDinamicos = {};
    let itemsOrden = [];
    let precioInscripcion;

    if (modalidad) {
      const columnasDinamicas = getDynamicColumnsForModalidad(modalidad, registryConfiguration);

      const rCampos = validarCamposDinamicos(columnasDinamicas, rawValues, categoriaMatch);
      errores.push(...rCampos.errores);
      advertencias.push(...rCampos.advertencias);
      camposDinamicos = rCampos.camposDinamicos;

      const rProductos = validarProductos(columnasDinamicas, rawValues);
      errores.push(...rProductos.errores);
      advertencias.push(...rProductos.advertencias);
      itemsOrden = rProductos.itemsOrden;

      precioInscripcion =
        modalidad.configModalidad &&
        modalidad.configModalidad.precio &&
        modalidad.configModalidad.precio.preventa_1
          ? modalidad.configModalidad.precio.preventa_1.precio_1
          : undefined;
      if (precioInscripcion === undefined) {
        errores.push("No se encontró el precio de inscripción configurado para esta modalidad.");
      }
    }

    const nombreCompleto = `${nombres} ${apellidos || `${apellidoPaterno} ${apellidoMaterno}`}`.trim();

    const valido = errores.length === 0;
    let participante = null;
    let montoTotal = 0;

    if (valido) {
      const order = [
        {
          code: "insc",
          description: `Inscripción al evento ${eventDetail ? eventDetail.nombreEvento : ""} - ${modalidad.descripcionModalidad}`,
          name: eventDetail ? eventDetail.nombreEvento : "",
          precioKey: "precio_1",
          quantity: 1,
          unit_price: precioInscripcion,
        },
        ...itemsOrden,
      ];

      if (pagoExterno) {
        const totalNominal = order.reduce((acc, item) => acc + item.unit_price * item.quantity, 0);
        order.push({
          code: "ajustePagoExterno",
          description: "Pago recibido y gestionado fuera de Dashport",
          name: "Ajuste pago externo",
          precioKey: "",
          quantity: 1,
          unit_price: -totalNominal,
        });
      }

      montoTotal = order.reduce((acc, item) => acc + item.unit_price * item.quantity, 0);

      participante = {
        nombres,
        ...(separarApellidos ? { apellidoPaterno, apellidoMaterno } : { apellidos }),
        rama: categoriaMatch.rama,
        categoria: categoriaMatch.categoria,
        correo,
        telefono,
        fechaNacimiento: fechaISO.replace(/-/g, "/"),
        modalidad: modalidad.codigoModalidad,
        ...(numero !== null ? { numero } : {}),
        monedaAUsar: MONEDA_BASE_DEFAULT,
        monedaBase: MONEDA_BASE_DEFAULT,
        order,
        mails: { sendMail: false },
        versionForm: "2.0",
        ...camposDinamicos,
      };
    }

    resultados.push({
      rowNumber,
      modalidadCategoriaTexto,
      nombreCompleto,
      valido,
      errores,
      advertencias,
      participante,
      montoTotal,
    });
  });

  return resultados;
}
